import { useCallback, useEffect, useRef, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PackagePlus, Plus, Loader2, FileUp, Trash2 } from "lucide-react";
import { PageHead, Empty, Field, inputCls, selectCls, btnPrimary, btnGhost, num, fmtDate, addDaysIso, qtyFmt } from "@/components/erp/ErpUi";
import { useDefaultMarkup, suggestPrice } from "@/components/erp/DefaultMarkup";

const NEW = "__new__";
const blankItem = () => ({ product_id: "", description: "", supplier_code: "", barcode: "", ncm: "", unit: "UN", qty: "", unit_cost: "", price: "" });

function ItemRow({ it, i, products, onChange, onRemove, defMk }) {
  const set = (k) => (e) => {
    const next = { ...it, [k]: e.target.value };
    if ((k === "unit_cost" || k === "product_id") && next.product_id === NEW && !it.price_touched) next.price = suggestPrice(num(next.unit_cost), defMk);
    if (k === "price") next.price_touched = true;
    onChange(i, next);
  };
  const isNew = it.product_id === NEW;
  return (
    <div data-testid={`purchase-item-${i}`} className="rounded-xl border border-zinc-800 p-3 space-y-2">
      {it.xml_description && <div className="text-[11px] text-zinc-500">Na nota: <span className="text-zinc-300">{it.xml_description}</span>{it.supplier_code ? ` · cód. ${it.supplier_code}` : ""}</div>}
      <div className="grid grid-cols-12 gap-2 items-center">
        <select data-testid={`purchase-item-${i}-product`} value={it.product_id} onChange={set("product_id")} className={`${selectCls} col-span-11`}>
          <option value="">Vincular a um produto…</option>
          <option value={NEW}>+ Criar produto novo</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.description}</option>)}
        </select>
        <button type="button" aria-label="Remover item" onClick={() => onRemove(i)} className="col-span-1 text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
      {isNew && <Input data-testid={`purchase-item-${i}-desc`} value={it.description} onChange={set("description")} placeholder="Descrição do produto novo" className={inputCls} />}
      <div className="grid grid-cols-3 gap-2">
        <Field label="Qtd"><Input data-testid={`purchase-item-${i}-qty`} value={it.qty} onChange={set("qty")} inputMode="decimal" className={inputCls} /></Field>
        <Field label="Custo un."><Input data-testid={`purchase-item-${i}-cost`} value={it.unit_cost} onChange={set("unit_cost")} inputMode="decimal" className={inputCls} /></Field>
        {isNew ? <Field label="Preço venda"><Input data-testid={`purchase-item-${i}-price`} value={it.price} onChange={set("price")} inputMode="decimal" className={inputCls} /></Field>
          : <div className="text-right text-sm text-zinc-300 self-end pb-2">{brl(num(it.qty) * num(it.unit_cost))}</div>}
      </div>
    </div>
  );
}

function PurchaseDialog({ open, onOpenChange, onSaved }) {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [xmlSupplier, setXmlSupplier] = useState(null);
  const [head, setHead] = useState({ nfe_number: "", nfe_key: "", issue_date: "" });
  const [xmlFiscal, setXmlFiscal] = useState(null);
  const [items, setItems] = useState([blankItem()]);
  const [dups, setDups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);
  const defMk = useDefaultMarkup(open);

  useEffect(() => {
    if (!open) return;
    setSupplierId(""); setXmlSupplier(null); setXmlFiscal(null); setHead({ nfe_number: "", nfe_key: "", issue_date: "" }); setItems([blankItem()]); setDups([]);
    api.get("/erp/products").then(({ data }) => setProducts(data.items)).catch(() => {});
    api.get("/erp/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
  }, [open]);

  const total = items.reduce((s, it) => s + num(it.qty) * num(it.unit_cost), 0);

  const onXml = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/erp/purchases/parse-xml", fd);
      if (data.already_imported) toast.warning("Atenção: essa nota já foi lançada antes");
      setHead({ nfe_number: data.nfe_number || "", nfe_key: data.nfe_key || "", issue_date: data.issue_date || "" });
      setSupplierId(data.supplier_id || ""); setXmlSupplier(data.supplier_id ? null : data.supplier); setXmlFiscal(data.fiscal || null);
      setItems(data.items.map((x, xi) => ({ ...blankItem(), xml_index: xi, product_id: x.product_id || NEW, description: x.description, xml_description: x.description,
        supplier_code: x.supplier_code || "", barcode: x.barcode || "", ncm: x.ncm || "", unit: x.unit || "UN", qty: String(x.qty), unit_cost: String(x.unit_cost), price: x.product_id ? "" : suggestPrice(x.unit_cost, defMk) })));
      setDups(data.duplicates.map((d) => ({ number: d.number || "", due_date: d.due_date, amount: String(d.amount) })));
      toast.success(`Nota ${data.nfe_number || ""} lida: ${data.items.length} item(ns)`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setParsing(false); }
  };

  const save = async () => {
    if (items.some((it) => !it.product_id || num(it.qty) <= 0)) { toast.error("Vincule todos os itens e informe a quantidade"); return; }
    setBusy(true);
    try {
      const body = {
        ...head, supplier_id: supplierId || null,
        supplier: !supplierId && xmlSupplier ? { name: xmlSupplier.name, trade_name: xmlSupplier.trade_name, cnpj: xmlSupplier.cnpj, phone: xmlSupplier.phone, city: xmlSupplier.city, uf: xmlSupplier.uf } : null,
        items: items.map((it) => ({
          product_id: it.product_id === NEW ? null : it.product_id, qty: num(it.qty), unit_cost: num(it.unit_cost), supplier_code: it.supplier_code || null,
          xml_index: xmlFiscal && Number.isInteger(it.xml_index) ? it.xml_index : null,
          new_product: it.product_id === NEW ? { description: it.description || "Produto", barcode: it.barcode, ncm: it.ncm, unit: it.unit, price: it.price === "" ? null : num(it.price) } : null,
        })),
        duplicates: dups.filter((d) => d.due_date && num(d.amount) > 0).map((d) => ({ ...d, amount: num(d.amount) })),
        fiscal: xmlFiscal && head.nfe_key ? xmlFiscal : null,
      };
      await api.post("/erp/purchases", body);
      toast.success("Compra lançada: estoque e contas a pagar atualizados"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const setDup = (i, k, v) => setDups((p) => p.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="purchase-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-2xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Entrada de mercadoria</DialogTitle>
          <DialogDescription className="text-zinc-400">Lance manualmente ou importe o XML da NF-e de compra.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input ref={fileRef} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={onXml} data-testid="purchase-xml-input" />
          <Button data-testid="purchase-xml-btn" type="button" variant="outline" className={btnGhost} disabled={parsing} onClick={() => fileRef.current?.click()}>
            {parsing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileUp className="h-4 w-4 mr-1" />} Importar XML da NF-e
          </Button>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Fornecedor" className="col-span-2 sm:col-span-3">
              <select data-testid="purchase-supplier" value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setXmlSupplier(null); }} className={selectCls}>
                <option value="">{xmlSupplier ? `Novo: ${xmlSupplier.name}` : "Sem fornecedor"}</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.trade_name || s.name}</option>)}
              </select>
            </Field>
            <Field label="Nº da nota"><Input data-testid="purchase-nfe-number" value={head.nfe_number} onChange={(e) => setHead((p) => ({ ...p, nfe_number: e.target.value }))} className={inputCls} /></Field>
            <Field label="Emissão"><input data-testid="purchase-issue-date" type="date" value={head.issue_date} onChange={(e) => setHead((p) => ({ ...p, issue_date: e.target.value }))} className={selectCls} /></Field>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => <ItemRow key={i} it={it} i={i} defMk={defMk} products={products} onChange={(idx, v) => setItems((p) => p.map((x, j) => (j === idx ? v : x)))} onRemove={(idx) => setItems((p) => p.filter((_, j) => j !== idx))} />)}
            <Button data-testid="purchase-add-item" type="button" variant="outline" className={btnGhost} onClick={() => setItems((p) => [...p, blankItem()])}><Plus className="h-4 w-4 mr-1" /> Item</Button>
          </div>
          <div className="rounded-xl border border-zinc-800 p-3 space-y-2">
            <div className="flex items-center justify-between"><span className="text-xs font-mono-caps text-zinc-400 uppercase">Contas a pagar (duplicatas)</span>
              <Button data-testid="purchase-add-dup" type="button" size="sm" variant="outline" className={btnGhost} onClick={() => setDups((p) => [...p, { number: "", due_date: addDaysIso(30), amount: String(Math.max(0, total - p.reduce((s, d) => s + num(d.amount), 0)).toFixed(2)) }])}><Plus className="h-3.5 w-3.5 mr-1" /> Vencimento</Button>
            </div>
            {dups.length === 0 && <p className="text-xs text-zinc-500">Sem vencimentos: nenhuma conta a pagar será criada (ex.: pago à vista).</p>}
            {dups.map((d, i) => (
              <div key={i} data-testid={`purchase-dup-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <input type="date" value={d.due_date} onChange={(e) => setDup(i, "due_date", e.target.value)} className={`${selectCls} col-span-6`} />
                <Input value={d.amount} onChange={(e) => setDup(i, "amount", e.target.value)} inputMode="decimal" className={`${inputCls} col-span-5`} />
                <button type="button" aria-label="Remover" onClick={() => setDups((p) => p.filter((_, j) => j !== i))} className="col-span-1 text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 sticky bottom-0 bg-zinc-950 py-2">
            <span className="font-display text-xl font-black text-amber-brand" data-testid="purchase-total">{brl(total)}</span>
            <Button data-testid="purchase-save" disabled={busy} onClick={save} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lançar compra"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OwnerPurchases() {
  const { user } = useAuth();
  const scopeAll = user?.shop_id === "all";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/erp/purchases").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={PackagePlus} kicker="Compras" title="Entrada de mercadoria">
        <Button data-testid="purchase-new-btn" disabled={scopeAll} className={btnPrimary} onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Nova compra</Button>
      </PageHead>
      {scopeAll && <p className="text-xs text-amber-300">Para lançar compras, escolha uma unidade no cabeçalho.</p>}
      {!items ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : items.length === 0 ? <Empty>Nenhuma compra lançada.</Empty> : (
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.id} data-testid={`purchase-row-${p.id}`} className="rounded-2xl border border-card2-border bg-card2 p-4">
              <div className="flex justify-between gap-2 flex-wrap">
                <div className="font-bold text-zinc-100">NF {p.nfe_number || "s/n"} · {p.supplier_name || "Sem fornecedor"}</div>
                <div className="font-display font-black text-amber-brand">{brl(p.total)}</div>
              </div>
              <div className="text-[11px] text-zinc-500">{fmtDate((p.issue_date || p.created_at || "").slice(0, 10))}{p.shop_name && scopeAll ? ` · ${p.shop_name}` : ""} · {p.items.length} item(ns)</div>
              <div className="text-xs text-zinc-400 mt-1 truncate">{p.items.map((i) => `${qtyFmt(i.qty)}× ${i.description}`).join(" · ")}</div>
            </li>
          ))}
        </ul>
      )}
      <PurchaseDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
